import { ButtonHTMLAttributes, ReactNode } from 'react'
import { ItemTile } from './item-tile'

/** @public */
export interface ItemGridItem<Id extends string = string> {
	id: Id
	/** Shown under the thumbnail when present. */
	name?: string
	/** The tile's accessible name: the item's name, or a description for an unnamed item. */
	accessibleLabel: string
	thumbnailUrl?: string
}

/** @public */
export interface ItemGridProps<Id extends string = string> {
	items: ItemGridItem<Id>[]
	/** Props for an item's tile button: its click, pointer and drag handlers. */
	getItemProps(id: Id): ButtonHTMLAttributes<HTMLButtonElement>
	/** An item's menu, shown over its tile. Leave it out for tiles without one. */
	renderItemMenu?(item: ItemGridItem<Id>): ReactNode
	/** The item being renamed, whose label is an input. */
	renamingId?: Id
	onRename?(id: Id, name: string): void
	onCancelRename?(): void
	/** Shown when there are no items. */
	empty: ReactNode
}

/** @public @react */
export function ItemGrid<Id extends string>({
	items,
	getItemProps,
	renderItemMenu,
	renamingId,
	onRename,
	onCancelRename,
	empty,
}: ItemGridProps<Id>) {
	if (items.length === 0) return <p className="tlui-collections-panel__empty">{empty}</p>

	return (
		<ul className="tlui-collections-grid">
			{items.map((item) => (
				<li key={item.id}>
					<ItemTile
						name={item.name}
						accessibleLabel={item.accessibleLabel}
						thumbnailUrl={item.thumbnailUrl}
						buttonProps={getItemProps(item.id)}
						// No menu while renaming: a closing menu hands focus back to its trigger, which would
						// blur the input straight away.
						menu={item.id === renamingId ? undefined : renderItemMenu?.(item)}
						renaming={
							item.id === renamingId && onRename && onCancelRename
								? {
										defaultValue: item.name ?? '',
										onComplete: (name) => onRename(item.id, name),
										onCancel: onCancelRename,
									}
								: undefined
						}
					/>
				</li>
			))}
		</ul>
	)
}
